import { useCreateImportedAccountsFromCoa } from "@api/accountingIntegration.api";
import { useProcessCoADocument } from "@api/fileScanning.api";
import { getAccountClassForType } from "@common/constants/domains/accounting/accountTypeHelpers";
import { AccountClass } from "@common/constants/domains/accounting/accounts";
import {
    AccountMapping,
    FinancialDocument,
} from "@common/types/financialDocument";
import { useAccountingTabContext } from "@components/accounting/useAccountingTab";
import { MapAccountsStep } from "@components/accounting/balanceSheet/BalanceSheetImporter/MapAccountsStep";
import { FinancialDocumentUploadProvider } from "@components/documents/FinancialDocumentUpload/FinancialDocumentUploadProvider";
import { usePreventBodyScroll } from "@design-system/Drawer/usePreventBodyScroll";
import {
    StandardModal,
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useScope } from "@state/scopeContext.lib";
import React, { useCallback, useMemo, useState } from "react";
import { CoaImporterModalContent } from "./CoaImporterModalContent";
import { CoaAccountRow, CoaImportStep, CoaValidationErrors } from "./types";
import styles from "./CoaAccountMapping.module.scss";

interface Props {
    show: boolean;
    onHide: () => void;
}

const DEFAULT_NUMBER_OF_LINES = 10;

function buildEmptyLines(
    size: number = DEFAULT_NUMBER_OF_LINES,
): CoaAccountRow[] {
    return new Array(size).fill(null).map(() => ({
        id: Math.random().toString(),
        name: "",
        accountClass: null,
    }));
}

function isFilledAccount(
    account: CoaAccountRow,
): account is CoaAccountRow & { accountClass: AccountClass } {
    return !!account.name?.trim() && !!account.accountClass;
}

function validateAccounts(accounts: CoaAccountRow[]): CoaValidationErrors {
    if (!accounts.some(isFilledAccount)) {
        return { noAccounts: "Add at least one account" };
    }

    const accountErrors = accounts.map((account) => {
        const hasName = !!account.name?.trim();
        const hasClass = !!account.accountClass;
        if ((hasName && hasClass) || (!hasName && !hasClass)) {
            return {};
        }
        return {
            name: !hasName ? "Missing name" : undefined,
            accountClass: !hasClass ? "Missing classification" : undefined,
        };
    });

    if (accountErrors.some((error) => error.name ?? error.accountClass)) {
        return { accounts: accountErrors };
    }

    return {};
}

export const CoaAccountMappingModal: React.FC<Props> = React.memo(
    ({ show, onHide }) => {
        const { toast } = useToaster();
        const { selectedEntity } = useAccountingTabContext();
        const { workspaceId } = useScope();
        const [accounts, setAccounts] =
            useState<CoaAccountRow[]>(buildEmptyLines());
        const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
        const [step, setStep] = useState<CoaImportStep>(
            CoaImportStep.UploadDocument,
        );

        usePreventBodyScroll(show);

        const processCoADocument = useProcessCoADocument();
        const createImportedAccounts = useCreateImportedAccountsFromCoa(
            workspaceId ?? "",
            selectedEntity?.id ?? 0,
        );

        const validationErrors = useMemo(
            () => (hasAttemptedSubmit ? validateAccounts(accounts) : {}),
            [accounts, hasAttemptedSubmit],
        );

        const footerErrorMessage = useMemo(() => {
            if (!hasAttemptedSubmit) {
                return undefined;
            }
            if (validationErrors.noAccounts) {
                return validationErrors.noAccounts;
            }
            if (validationErrors.accounts?.length) {
                return "Please complete the highlighted fields";
            }
            return undefined;
        }, [hasAttemptedSubmit, validationErrors]);

        const resetState = useCallback(() => {
            setAccounts(buildEmptyLines());
            setHasAttemptedSubmit(false);
            setStep(CoaImportStep.UploadDocument);
        }, []);

        const handleSave = useCallback(() => {
            toast("Accounts mapped");
            resetState();
            onHide();
        }, [toast, onHide, resetState]);

        const handleHide = useCallback(() => {
            resetState();
            onHide();
        }, [onHide, resetState]);

        const loadExtractionIntoState = useCallback(
            (mappings: AccountMapping[]) => {
                const newAccounts: CoaAccountRow[] = mappings.map(
                    (mapping) => ({
                        id: Math.random().toString(),
                        name: mapping.originalAccountName,
                        accountClass: mapping.mappedAccountType
                            ? getAccountClassForType(mapping.mappedAccountType)
                            : null,
                    }),
                );

                setAccounts([...newAccounts, ...buildEmptyLines()]);
                setStep(CoaImportStep.EnterAccounts);
            },
            [],
        );

        const handleDocumentSelect = useCallback(
            async (documentId: number) => {
                if (!selectedEntity) {
                    toast("Select an entity first");
                    return;
                }

                try {
                    const result = await processCoADocument.mutateAsync({
                        entityId: selectedEntity.id,
                        documentId,
                    });

                    if (!result.success || !result.accountMappings) {
                        throw new Error("Invalid response format");
                    }

                    loadExtractionIntoState(
                        result.accountMappings.accountMappings,
                    );
                } catch (uploadError) {
                    toast(
                        `Failed to process file: ${uploadError instanceof Error ? uploadError.message : "Please try again."}`,
                    );
                }
            },
            [
                selectedEntity,
                processCoADocument,
                toast,
                loadExtractionIntoState,
            ],
        );

        const handleUploadCompleted = useCallback(
            async (uploadedDocuments: FinancialDocument[]) => {
                const financialDocument = uploadedDocuments[0];
                if (financialDocument) {
                    await handleDocumentSelect(financialDocument.id);
                }
            },
            [handleDocumentSelect],
        );

        const handleStepChange = useCallback(
            async (newStep: CoaImportStep) => {
                if (
                    newStep === CoaImportStep.MapAccounts &&
                    step === CoaImportStep.EnterAccounts
                ) {
                    setHasAttemptedSubmit(true);
                    const errors = validateAccounts(accounts);
                    if (Object.keys(errors).length > 0) {
                        return;
                    }

                    if (!selectedEntity || !workspaceId) {
                        toast("Select an entity first");
                        return;
                    }

                    const filledAccounts = accounts.filter(isFilledAccount);

                    try {
                        await createImportedAccounts.mutateAsync({
                            accounts: filledAccounts.map((account) => ({
                                name: account.name.trim(),
                                classification: account.accountClass,
                            })),
                        });
                        setAccounts(filledAccounts);
                        setStep(newStep);
                    } catch (error) {
                        toast(
                            `Failed to create account mapping: ${error instanceof Error ? error.message : "Please try again."}`,
                        );
                    }
                    return;
                }

                if (newStep === CoaImportStep.UploadDocument) {
                    setHasAttemptedSubmit(false);
                }

                setStep(newStep);
            },
            [
                step,
                accounts,
                selectedEntity,
                workspaceId,
                createImportedAccounts,
                toast,
            ],
        );

        const renderContent = () => {
            if (step === CoaImportStep.MapAccounts && selectedEntity) {
                return (
                    <>
                        <StandardModalHeader>
                            Import & map accounts
                        </StandardModalHeader>
                        <StandardModalBody className={styles.modalBody}>
                            <div className={styles.mapStepContainer}>
                                <MapAccountsStep
                                    entity={selectedEntity}
                                    onSave={handleSave}
                                />
                            </div>
                        </StandardModalBody>
                    </>
                );
            }

            return (
                <FinancialDocumentUploadProvider
                    onCompleted={handleUploadCompleted}
                >
                    <CoaImporterModalContent
                        step={step}
                        entityName={selectedEntity?.name ?? undefined}
                        accounts={accounts}
                        onAccountsChange={setAccounts}
                        validationErrors={validationErrors}
                        hasAttemptedSubmit={hasAttemptedSubmit}
                        isExtracting={processCoADocument.isPending}
                        isCreating={createImportedAccounts.isPending}
                        canUpload={!!selectedEntity && !!workspaceId}
                        onDocumentSelect={handleDocumentSelect}
                        onStepChange={handleStepChange}
                        footerErrorMessage={footerErrorMessage}
                    />
                </FinancialDocumentUploadProvider>
            );
        };

        return (
            <StandardModal
                fullscreen
                onHide={handleHide}
                show={show}
                disableAnimation
            >
                {renderContent()}
            </StandardModal>
        );
    },
);
